import { supabase } from "./supabaseClient";
import type { ActionCard } from "../types/cards";

const API_BASE = import.meta.env.VITE_API_BASE ?? "/api";

export interface ChatMessage {
  role: "user" | "assistant";
  content: string;
}

interface StreamOptions {
  messages: ChatMessage[];
  // Groups action cards born in the same chat session (worker stores
  // it on the card row). Optional — dashboard-era callers omit it.
  conversationId?: string;
  onDelta: (delta: string) => void;
  // Fired the instant the worker detects a card is coming (sentinel
  // reached), before the JSON finishes streaming + persisting — lets
  // the UI show a placeholder so the reply never points at an empty
  // column.
  onCardPending?: () => void;
  // Fired when the worker persisted an action card generated for this
  // turn. replacedCardId names the pending card it superseded, if any.
  onCard?: (card: ActionCard, replacedCardId: string | null) => void;
  // The model attempted a card but it failed validation/persistence.
  onCardError?: () => void;
  signal?: AbortSignal;
}

// POST /api/chat with auth, then read the SSE response. Text tokens
// arrive in OpenRouter's delta shape; the worker may additionally
// emit `{"type":"card",...}` / `{"type":"card_error"}` events before
// the trailing `data: [DONE]`. Resolves when the stream ends; rejects
// on network error or non-2xx HTTP. Throws AbortError if the caller
// aborts via the signal.
export async function streamChat(opts: StreamOptions): Promise<void> {
  const auth = await authHeader();
  const res = await fetch(`${API_BASE}/chat`, {
    method: "POST",
    credentials: "include",
    cache: "no-store",
    headers: {
      "content-type": "application/json",
      accept: "text/event-stream",
      ...auth,
    },
    body: JSON.stringify({
      messages: opts.messages,
      ...(opts.conversationId ? { conversationId: opts.conversationId } : {}),
    }),
    signal: opts.signal,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(humanizeChatError(res.status, text));
  }
  if (!res.body) {
    throw new Error("Empty response from chat backend.");
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder("utf-8");
  // SSE events are separated by `\n\n`. Network chunks split events
  // arbitrarily, so buffer across reads and only emit complete events.
  let buf = "";
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    let sep: number;
    while ((sep = buf.indexOf("\n\n")) !== -1) {
      const event = buf.slice(0, sep);
      buf = buf.slice(sep + 2);
      dispatchSseEvent(event, opts);
    }
  }
  // Flush trailing event if the upstream didn't send the final \n\n.
  if (buf.trim()) dispatchSseEvent(buf, opts);
}

// One SSE event can contain multiple lines; we only care about
// `data:` lines that aren't the sentinel `[DONE]`. Each is JSON —
// either a chat-completions delta or one of the worker's card events.
function dispatchSseEvent(event: string, opts: StreamOptions): void {
  for (const line of event.split("\n")) {
    if (!line.startsWith("data:")) continue;
    const data = line.slice(5).trim();
    if (!data || data === "[DONE]") continue;
    try {
      const parsed = JSON.parse(data) as {
        choices?: Array<{ delta?: { content?: string } }>;
        type?: string;
        card?: ActionCard;
        replacedCardId?: string | null;
      };
      const content = parsed.choices?.[0]?.delta?.content;
      if (content) {
        opts.onDelta(content);
      } else if (parsed.type === "card_pending") {
        opts.onCardPending?.();
      } else if (parsed.type === "card" && parsed.card) {
        opts.onCard?.(parsed.card, parsed.replacedCardId ?? null);
      } else if (parsed.type === "card_error") {
        opts.onCardError?.();
      }
    } catch {
      // Malformed `data:` lines are rare; ignore so a single bad
      // chunk doesn't kill the stream.
    }
  }
}

function humanizeChatError(status: number, text: string): string {
  if (status === 401) return "Your session expired. Sign in again to keep chatting.";
  if (status === 429) return "You've hit today's chat limit. Try again tomorrow.";
  if (status === 502) return "The chat backend isn't reachable. Try again in a minute.";
  return `Chat error ${status}: ${text.slice(0, 200)}`;
}

async function authHeader(): Promise<Record<string, string>> {
  if (!supabase) return {};
  const { data } = await supabase.auth.getSession();
  if (!data.session) return {};
  return { authorization: `Bearer ${data.session.access_token}` };
}
